import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader, Chip, statusVariant, EmptyState } from "@/components/ui-bits";
import {
  Repeat, ArrowRight, Users as UsersIcon, Clock, Send, Inbox, CheckCircle2,
  XCircle, MessageCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import ManagerOnlineHint from "@/components/ManagerOnlineHint";

// Small helper: initials from a name
const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

// Human-friendly date + time from shift_summary "YYYY-MM-DD HH:MM–HH:MM"
function parseSummary(s) {
  if (!s) return { date: "", time: "" };
  const [date, ...rest] = s.split(" ");
  return { date, time: rest.join(" ") };
}
function humanDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("nl", { weekday: "short", day: "numeric", month: "short" });
}

const TABS = (t) => [
  { id: "all", label: t("tab_all"), Icon: UsersIcon },
  { id: "sent", label: t("tab_sent"), Icon: Send },
  { id: "received", label: t("tab_received"), Icon: Inbox },
  { id: "done", label: t("tab_done"), Icon: CheckCircle2 },
];

const SEEN_KEY = "rostr:myswaps:seen:v1";
const readSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
};
const writeSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch {}
};

export default function MySwaps() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);
  const [seen, setSeen] = useState(() => readSeen());

  const load = async () => setList((await api.get("/swap-requests")).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/swap-requests/${id}/action`, { action });
      toast.success(t("saved"));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally { setBusy(null); }
  };

  const enriched = useMemo(() => list.map((s) => ({
    ...s,
    mine: s.from_employee_id === user?._id,
    active: ["pending", "accepted"].includes(s.status),
  })), [list, user]);

  // Only count UNSEEN items — dots disappear once the tab has been viewed
  const counts = useMemo(() => {
    const unseen = enriched.filter((s) => !seen.has(s._id));
    return {
      all: unseen.length,
      sent: unseen.filter((s) => s.mine).length,
      received: unseen.filter((s) => !s.mine).length,
      done: unseen.filter((s) => !s.active).length,
    };
  }, [enriched, seen]);

  const filtered = useMemo(() => {
    switch (tab) {
      case "sent": return enriched.filter((s) => s.mine);
      case "received": return enriched.filter((s) => !s.mine);
      case "done": return enriched.filter((s) => !s.active);
      default: return enriched;
    }
  }, [enriched, tab]);

  // Mark items visible in the current tab as seen — clears the badge dot
  useEffect(() => {
    if (!filtered.length) return;
    const ids = filtered.map((s) => s._id).filter(Boolean);
    if (!ids.length) return;
    setSeen((prev) => {
      let changed = false;
      const next = new Set(prev);
      ids.forEach((id) => { if (!next.has(id)) { next.add(id); changed = true; } });
      if (changed) writeSeen(next);
      return changed ? next : prev;
    });
  }, [filtered]);

  // Split active vs history for the "all" and per-role tabs
  const active = filtered.filter((s) => s.active);
  const history = filtered.filter((s) => !s.active);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="page-my-swaps">
      <ManagerOnlineHint />
      <PageHeader
        eyebrow={t("my_swaps")}
        title={t("swap_page_title")}
        subtitle={t("swap_page_subtitle")}
      />

      {/* Tabs — icon-only segmented control, full-width */}
      <div className="card-glass p-1 grid grid-cols-4 gap-1 w-full" data-testid="myswap-tabs">
        {TABS(t).map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            data-testid={`myswap-tab-${tt.id}`}
            title={tt.label}
            aria-label={tt.label}
            className={`relative flex items-center justify-center h-10 rounded-full transition
              ${tab === tt.id
                ? "bg-[hsl(var(--primary))] text-white shadow-[var(--shadow-sm)]"
                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))] hover:bg-[hsl(var(--muted))]"}`}
          >
            <tt.Icon size={17} strokeWidth={1.9} />
            {counts[tt.id] > 0 && (
              <span
                className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full grid place-items-center text-[10px] font-bold tabular-nums leading-none
                  ${tab === tt.id
                    ? "bg-white text-[hsl(var(--primary))]"
                    : "bg-[hsl(var(--primary))] text-white"}`}
              >
                {counts[tt.id]}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Active tab label for context */}
      <div className="text-[12px] text-[hsl(var(--text-muted))] -mt-4 pl-1" data-testid="myswap-tab-label">
        {TABS(t).find((x) => x.id === tab)?.label}
      </div>

      {filtered.length === 0 && (
        <EmptyState type="sent"
          icon={Repeat}
          title={t("no_swaps_title")}
          hint={t("no_swaps_hint")}
        />
      )}

      {active.length > 0 && (
        <Section title={t("in_progress_section")} hint={`${active.length} ${t("active_count")}`}>
          <div className="flex flex-col gap-3">
            {active.map((s) => (
              <SwapCard key={s._id} s={s} busy={busy === s._id} onDecide={decide} />
            ))}
          </div>
        </Section>
      )}

      {history.length > 0 && (
        <Section title={t("history_section")} hint={`${history.length} ${t("done_count")}`}>
          <div className="flex flex-col gap-3">
            {history.map((s) => (
              <SwapCard key={s._id} s={s} busy={false} onDecide={decide} history />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

// ---------- Sub-components ----------
function Section({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-3">
        <div className="eyebrow">{title}</div>
        <div className="text-[11px] text-[hsl(var(--text-muted))]">{hint}</div>
      </div>
      {children}
    </section>
  );
}

function SwapCard({ s, busy, onDecide, history }) {
  const { t } = useI18n();
  const { date, time } = parseSummary(s.shift_summary);
  const targetName = s.target_employee_name || t("everyone_label");
  const from = s.mine ? { name: t("you"), initials: t("you").slice(0, 2).toUpperCase() } : { name: s.from_employee_name, initials: initials(s.from_employee_name) };
  const to = s.mine ? { name: targetName, initials: initials(targetName) } : { name: t("you"), initials: t("you").slice(0, 2).toUpperCase() };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      className={`card p-4 md:p-6 relative ${history ? "opacity-90" : ""}`}
      data-testid={`my-swap-${s._id}`}
    >
      {/* Status chip pinned top-right */}
      <div className="absolute top-3 right-3 md:top-4 md:right-4">
        <Chip variant={statusVariant(s.status)}>{t(s.status)}</Chip>
      </div>

      {/* From → To (compact avatar + label pair, wraps on small) */}
      <div className="pr-24 md:pr-32 grid grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-2">
        <Avatar name={from.name} initials={from.initials} highlight={s.mine} />
        <div className="text-[hsl(var(--text-muted))]"><ArrowRight size={14} strokeWidth={2} /></div>
        <Avatar name={to.name} initials={to.initials} highlight={!s.mine} />
        <div className="col-span-3 flex items-center gap-2 text-[12px] text-[hsl(var(--text-muted))]">
          <span className="font-semibold text-[hsl(var(--text-strong))] truncate max-w-[9rem]">{from.name}</span>
          <ArrowRight size={11} strokeWidth={2} />
          <span className="font-semibold text-[hsl(var(--text-strong))] truncate max-w-[9rem]">{to.name}</span>
        </div>
      </div>

      {/* Shift details */}
      <div className="mt-4 flex items-center gap-3 rounded-2xl px-3.5 py-3 bg-[hsl(var(--muted))]">
        <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
          <Clock size={15} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-[hsl(var(--text-muted))] uppercase tracking-wider">{t("shift_label")}</div>
          <div className="text-[14px] font-bold text-[hsl(var(--text-strong))] tabular-nums leading-tight">
            {humanDate(date)}<span className="text-[hsl(var(--text-muted))] font-medium"> · </span>{time}
          </div>
        </div>
      </div>

      {/* Message */}
      {s.message && (
        <div className="mt-3 flex items-start gap-2 text-[13px] text-[hsl(var(--text-muted))] leading-relaxed">
          <MessageCircle size={13} strokeWidth={1.8} className="mt-0.5 shrink-0" />
          <div className="italic break-words min-w-0">"{s.message}"</div>
        </div>
      )}

      {/* Actions */}
      {!history && (
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          {!s.mine && s.status === "pending" && (
            <>
              <button
                onClick={() => onDecide(s._id, "decline")}
                disabled={busy}
                className="btn btn-ghost !h-10 flex-1 md:flex-none"
                data-testid={`decline-swap-${s._id}`}
              >
                <XCircle size={14} /> {t("decline")}
              </button>
              <button
                onClick={() => onDecide(s._id, "accept")}
                disabled={busy}
                className="btn btn-primary !h-10 flex-1 md:flex-none"
                data-testid={`accept-swap-${s._id}`}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {t("accept")}
              </button>
            </>
          )}
          {s.mine && s.status === "pending" && (
            <button
              onClick={() => onDecide(s._id, "cancel")}
              disabled={busy}
              className="btn btn-ghost !h-10 w-full md:w-auto"
              data-testid={`cancel-swap-${s._id}`}
            >
              <XCircle size={14} /> {t("cancel_request")}
            </button>
          )}
          {s.status === "accepted" && (
            <div className="text-[12px] text-[hsl(var(--text-muted))] italic self-center flex-1 md:flex-none">
              {t("waiting_manager")}
            </div>
          )}
        </div>
      )}

      {/* Footer: created_at */}
      {s.created_at && (
        <div className="mt-3 pt-3 border-t hairline text-[11px] text-[hsl(var(--text-muted))]">
          {t("created_at")} {new Date(s.created_at).toLocaleDateString("nl", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </motion.div>
  );
}

function Avatar({ name, initials, highlight }) {
  return (
    <div
      title={name}
      className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ring-2
        ${highlight ? "bg-[hsl(var(--primary))] text-white ring-[hsl(var(--primary-soft))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-strong))] ring-white"}`}
    >
      {initials || "?"}
    </div>
  );
}
